import React, { ReactElement, CSSProperties, useMemo } from 'react';
import { Layout as ReactGridLayout } from 'react-grid-layout';
import styled from 'styled-components';
import Widget, { WidgetType } from 'domain/entity/widget';
import { LayoutType, LayoutElementType, ConstraintsPerLayoutElement } from 'domain/entity/layout';
import WidgetContainer from 'adapter/in/common/containers/Widget';
import ReactGrid, { GridLayoutElement, GridLayouts } from 'adapter/in/dashboards/components/generic/ReactGrid';
import i18n from 'asset/translation';
import { GridOrigin, elementFitsContraints } from 'adapter/in/dashboards/containers/Grid';
import { WidgetBeingResized } from 'adapter/in/dashboards/containers/Grid/useGridView';
import Dashboard from 'domain/entity/dashboard';
import { useLayoutsView } from 'adapter/in/common/context/Layouts/LayoutsContext';
import { mapLayoutWidgetsToGridElements } from 'adapter/in/dashboards/components/Grid/gridUtils';
import { Option } from 'fp-ts/lib/Option';
import { Loading } from '@dexma/ui-components';
import { isDashboardFrame } from 'adapter/in/common/widget/widgetBuilder';
import { FrameWidgetData } from 'adapter/in/reports/components/CommonReportGrid/CommonReportGrid';

const BREAKPOINTS_PADDING = 48;
export const A4_PADDING_X = 42;
export const A4_PADDING_Y = 21;

export const BREAKPOINTS = {
    [LayoutType.DESKTOP]: 1200 - BREAKPOINTS_PADDING,
    [LayoutType.TABLET]: 996 - BREAKPOINTS_PADDING,
    [LayoutType.MOBILE]: 768 - BREAKPOINTS_PADDING,
    [LayoutType.A4]: 940,
};
export const CELL_MARGIN = 10;
export const RATIO = 1.5;
export const COLUMNS = {
    [LayoutType.DESKTOP]: 12,
    [LayoutType.TABLET]: 6,
    [LayoutType.MOBILE]: 4,
    [LayoutType.A4]: 12,
};
export const A4_PAGE_HEIGHT = 1366;
export const A4_ROWS_PER_PAGE = 36;
export const A4_HEADER_ROWS = 2;
export const A4_FOOTER_ROWS = 1;
export const A4_CONTENT_START_ROW = A4_HEADER_ROWS; // 2
export const A4_CONTENT_END_ROW = A4_ROWS_PER_PAGE - A4_FOOTER_ROWS - 1; // 34
export const A4_CELL_HEIGHT = (A4_PAGE_HEIGHT - (A4_ROWS_PER_PAGE - 1) * CELL_MARGIN) / A4_ROWS_PER_PAGE; // aprox. 28.22

export type Breakpoint = LayoutType.DESKTOP | LayoutType.TABLET | LayoutType.MOBILE | LayoutType.A4;

export type GridLayoutsAndWidgetMap = { gridLayouts: GridLayouts; widgetMap: Map<string, Widget> };
type GridProps = {
    dashboardId: string;
    isEditEnabled: boolean;
    widgetMap: Map<string, Widget>;
    gridLayouts: GridLayouts;
    cellSize: {
        width: number;
        height: number;
    };
    backgroundStyle: CSSProperties;
    currentBreakpoint: Breakpoint;
    breakpoints: { [P: string]: number };
    containerWidth: number;
    onWidthChange: (newWidth: number) => void;
    onLayoutChange: (currentLayout: GridLayoutElement[], allLayouts: GridLayouts) => void;
    onResize: (layout: ReactGridLayout[], oldItem: ReactGridLayout, newItem: ReactGridLayout) => void;
    onResizeStop: (layout: ReactGridLayout[], oldItem: ReactGridLayout, newItem: ReactGridLayout) => void;
    onDragStart: () => void;
    onDragStop: () => void;
    onBreakpointChange: (newBreakpoint: Breakpoint) => void;
    wrongSizeElements: Record<LayoutType, Record<string, ConstraintsPerLayoutElement>>;
    updateFrameDependencies?: {
        updateFrameData: Option<Dashboard>;
        updateFrameIsLoading: boolean;
    };
    editingLayout?: boolean;
    frameData?: FrameWidgetData;
    widgetBeingResized?: WidgetBeingResized;
    gridOrigin?: GridOrigin;
};

const WidgetContainerWrapper = styled.div<{ $widgetId: string }>`
    .ProseMirror {
        display: ${({ $widgetId }) => ($widgetId.startsWith('__a4_footer') || $widgetId.startsWith('__a4_header') ? 'flex' : '')};
        flex-direction: ${({ $widgetId }) => ($widgetId.startsWith('__a4_footer') || $widgetId.startsWith('__a4_header') ? 'row' : '')};

        align-items: ${({ $widgetId }) => ($widgetId.startsWith('__a4_header') ? 'center' : '')};

        justify-content: ${({ $widgetId }) => ($widgetId.startsWith('__a4_footer') ? 'space-between' : '')};

        img {
            ${({ $widgetId }) => ($widgetId.startsWith('__a4_header') ? 'margin-right: 10px; height: 30px; width: auto;' : '')};
        }

        p {
            ${({ $widgetId }) =>
                $widgetId.startsWith('__a4_header')
                    ? 'margin-top: 0; margin-bottom: 0; display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden;'
                    : ''};
        }
    }
`;

const Grid = (props: GridProps): ReactElement => {
    const {
        dashboardId,
        cellSize,
        backgroundStyle,
        widgetMap,
        gridLayouts,
        currentBreakpoint,
        breakpoints,
        containerWidth,
        onResize,
        onResizeStop,
        onDragStart,
        onDragStop,
        onWidthChange,
        onLayoutChange,
        onBreakpointChange,
        isEditEnabled,
        wrongSizeElements,
        frameData,
        updateFrameDependencies,
        widgetBeingResized,
        gridOrigin = GridOrigin.DASHBOARD,
        editingLayout,
    } = props;

    const RICH_TEXT_TYPES = new Set([LayoutElementType.RICH_TEXT, LayoutElementType.A4_HEADER, LayoutElementType.A4_FOOTER]);

    const { layouts } = useLayoutsView();
    const getWidgetObject = (widgetLayout: GridLayoutElement) => {
        if (widgetLayout.type === LayoutElementType.WIDGET && widgetMap.get(widgetLayout.i)) {
            return widgetMap.get(widgetLayout.i) as Widget;
        }
        return {
            id: widgetLayout.i,
            name: '',
            type: RICH_TEXT_TYPES.has(widgetLayout.type) ? WidgetType.RICH_TEXT : WidgetType.ANALYSIS,
            presetKey: '',
            optionFields: [],
            configurationFields: [],
        };
    };

    const layoutToRender = useMemo(() => {
        const layoutFromContext = layouts.get(currentBreakpoint);

        if (!layoutFromContext) {
            return getCurrentLayout(gridLayouts, currentBreakpoint) ?? [];
        }

        return mapLayoutWidgetsToGridElements(layoutFromContext, editingLayout);
    }, [layouts, currentBreakpoint, gridLayouts]);

    return (
        <ReactGrid
            gridLayouts={{
                ...gridLayouts,
                [currentBreakpoint]: layoutToRender,
            }}
            cellMargin={[CELL_MARGIN, CELL_MARGIN]}
            columns={COLUMNS}
            breakpoints={breakpoints}
            rowHeight={cellSize.height}
            backgroundStyle={backgroundStyle}
            containerWidth={containerWidth}
            onDrag={onDragStart}
            onResize={onResize}
            onResizeStop={onResizeStop}
            onDragStop={onDragStop}
            onWidthChange={onWidthChange}
            onLayoutChange={onLayoutChange}
            onBreakpointChange={(newBreakpoint: string) => onBreakpointChange(newBreakpoint as Breakpoint)}
        >
            {layoutToRender.map((widgetLayout) => {
                const isCorrectlyBeingResized =
                    widgetBeingResized && widgetBeingResized.i === widgetLayout.i ? widgetBeingResized.fitsInGrid : true;
                const isCorrectlySetInGrid = elementFitsContraints(widgetLayout);
                const widget = getWidgetObject(widgetLayout);
                if (updateFrameDependencies?.updateFrameIsLoading && isDashboardFrame(widgetLayout.i, widgetLayout.type)) {
                    return (
                        <WidgetContainerWrapper key={widgetLayout.i} $widgetId={widgetLayout.i}>
                            <Loading isLoading size={20} />
                        </WidgetContainerWrapper>
                    );
                }
                const page = currentBreakpoint === LayoutType.A4 ? Math.floor(widgetLayout.y / A4_ROWS_PER_PAGE) : 0;
                return (
                    <WidgetContainerWrapper key={widgetLayout.i} data-widget-id={widgetLayout.i} $widgetId={widgetLayout.i}>
                        <WidgetContainer
                            data-widget-page={page}
                            widgetPage={page}
                            dashboardId={dashboardId}
                            frameData={frameData}
                            updateFrameData={updateFrameDependencies?.updateFrameData}
                            type={widgetLayout.type}
                            widget={widget}
                            isEditingLayout={!widgetLayout.static}
                            isEditEnabled={isEditEnabled}
                            layoutType={currentBreakpoint}
                            gridOrigin={gridOrigin}
                            alertTooltip={getAlertTooltip(widgetLayout.i, wrongSizeElements, currentBreakpoint, isEditEnabled)}
                            sizeIssue={
                                isCorrectlySetInGrid && isCorrectlyBeingResized
                                    ? undefined
                                    : {
                                          maxHeight: widgetLayout.maxHeight,
                                          maxWidth: widgetLayout.maxWidth,
                                          minHeight: widgetLayout.minHeight,
                                          minWidth: widgetLayout.minWidth,
                                          type: currentBreakpoint,
                                      }
                            }
                        />
                    </WidgetContainerWrapper>
                );
            })}
        </ReactGrid>
    );
};

const getAlertTooltip = (
    widgetId: string,
    wrongSizeWidgets: Record<LayoutType, Record<string, ConstraintsPerLayoutElement>>,
    currentBreakpoint: Breakpoint,
    isEditEnabled: boolean
): string | undefined => {
    let wrongLayoutTypes: (string | undefined)[] = [];
    if (isEditEnabled && !wrongSizeWidgets[currentBreakpoint][widgetId]) {
        const wrongSizeWidgetsCopy = { ...wrongSizeWidgets };
        delete wrongSizeWidgetsCopy[currentBreakpoint];
        wrongLayoutTypes = Object.keys(wrongSizeWidgetsCopy)
            .map((layoutType) => {
                if (
                    Object.keys(wrongSizeWidgetsCopy[layoutType as LayoutType]).find(
                        (wrongSizeWidgetId: string) => wrongSizeWidgetId === widgetId
                    )
                ) {
                    return i18n.t(`dashboards.layouts.${layoutType.toLowerCase()}SaveFeedback`);
                }
                return undefined;
            })
            .filter((layoutType) => layoutType !== undefined);
    }
    return wrongLayoutTypes.length > 0
        ? i18n.t('widgets.alertWrongSizeOnLayouts', {
              layoutTypes: wrongLayoutTypes.toString().toLowerCase().replaceAll(',', ', '),
          })
        : undefined;
};

const getCurrentLayout = (reactGridLayouts: GridLayouts, breakpoint: LayoutType) =>
    reactGridLayouts[breakpoint] ?? reactGridLayouts[LayoutType.DESKTOP];

export default Grid;
